/*
 * JuvaBun cart (drawer + cart page). Vanilla JS, no dependencies.
 *
 * - <jb-cart-drawer id="cart-drawer"> (sections/cart-drawer.liquid) and <jb-cart-page> (sections/main-cart.liquid)
 *   both wrap a [data-jbc-root] rendered by snippets/cart-jb-body.liquid.
 * - Every change is ONE request to /cart/change.js or /cart/update.js with the "sections" param; the response
 *   re-renders the roots. The UI updates optimistically before the response arrives.
 * - All cart writes (ours and the jb PDP / bundle builder adds via JBCart.add) run one at a time through `net`,
 *   which recovers from network-level failures (iOS Safari "Load failed") without showing raw errors or
 *   doubling an add. See the "network" block below.
 * - Works with Impact's product forms: listens to "variant:add" and "cart:change" (and "cart:refresh",
 *   "cart:updated" from other scripts), registers its section for "cart:prepare-bundled-sections", and
 *   re-dispatches "cart:change" after its own changes so <cart-count> and other theme listeners stay in sync.
 * - Shipping protection is ON by default (owner decision): while the cart has products and the shopper has not
 *   opted out (and the cart has no bundle builder lines, which go straight to checkout without it), store.reconcile keeps exactly one protection line in the cart. Unchecking removes it and stores the
 *   opt-out for this cart (cart attribute _jb_prot_optout=1 + localStorage backup); checking again clears it.
 *   Always via /cart/update.js (never fires Klaviyo "Added to Cart"), always through the serialized queue.
 * - Checkout stays a normal form POST to /cart with name="checkout" (OCU hooks it).
 * - "Build your bundle" lines (property _jb_bb, snippets/cart-jb-bundle.liquid) are removed as a whole bundle:
 *   "Remove bundle" (or any Remove inside the group) sets every line of that bundle to 0 in one /cart/update.js call.
 * - Discount codes we manage (store.plan): the bundle builder code, and the Messy Bun page codes (JBM2 / JBM3 / JBMB,
 *   decided from the _jb_bundle units of the Messy Bun). Only these are ever added or removed; any other code in the
 *   cart (customer codes, the bun wardrobe page's BUNSET2 / BUNSET3 / STAYPUTKIT) is always kept.
 * - Pages with the self-contained bun wardrobe (#jbx, its own cart and codes): no automatic cart writes at all
 *   (no protection add, no code sync) and our drawer never opens there. A #jbx page that uses the theme cart opts in
 *   with the attribute data-jb-theme-cart on #jbx (the JuvaBun Club page v2, 2026-10-25): normal cart behavior there.
 * - Membership lines (Club page v2, Amit 2026-10-25): JuvaBun Club / JuvaBun Pass (handle juvabun-club / juvabun-pass,
 *   SKU BUN-CLUB / BUN-PASS-90; isMembership below) are shown as normal lines and counted in the item count, but they
 *   are not goods: they never trigger shipping protection (a membership-only cart gets none, and loses it when the
 *   last real product leaves) and they do not count toward the free-shipping bar (recalc skips [data-memb] lines).
 *   requires_shipping is NOT used: many real products (Small Messy Bun, Straight Bun, brushes, clips) and protection
 *   itself have requires_shipping false in Shopify. snippets/cart-jb-body.liquid applies the same rule server side
 *   (protection box, free-shipping bar, T3 jbc1 slot). The Messy Bun codes / gift (mbPlan) only count MB_PID lines.
 */
(() => {
  if (window.JBCart) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const DEBOUNCE_MS = 350;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- money */
  function formatMoney(cents) {
    const settings = (window.themeVariables && window.themeVariables.settings) || {};
    const format = String(settings.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents) || 0;
    const fmt = (n, decimals, thousands, decimal) => {
      const fixed = (n / 100).toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2, ',', '.');
      }
    });
  }

  /* ------------------------------------------------------------- network
     All our cart writes (PDP adds, bundle builder, drawer qty/remove/protection, bundle code sync) go through
     net.enqueue, so only one runs at a time and none can race or cancel another.
     A fetch that fails at the network level (iOS Safari "TypeError: Load failed", typically a stale keep-alive
     connection after the tab sat idle or in the background) is never shown to the shopper. For an add we first
     check /cart.js against a snapshot to see whether the add went through anyway (request reached Shopify, the
     response was lost) and only re-send it when it did not, so a retry can never double the items. Quantity and
     update calls set absolute values, so re-sending them is always safe. HTTP 4xx (e.g. 422 sold out) shows
     Shopify's own description right away, no retry. */
  const NET_FAIL_MSG = "We couldn't reach the store. Please check your connection and tap again.";
  const GENERIC_MSG = 'Sorry, something went wrong. Please try again.';
  const RETRY_DELAYS = [600, 1200]; // max 2 retries
  const CHECK_DELAY = 500;
  const POST_TIMEOUT = 20000;
  const SNAPSHOT_MAX_AGE = 120000;
  const OPTOUT_ATTR = '_jb_prot_optout';
  const OPTOUT_LS = 'jb-prot-optout';
  const FREE_TODAY_PID = 9415365394645;
  /* T20 Shipping Protection $2.99 vs $3.49 (snippets/jb-t20-loader.liquid sets window.JBT20_ARM from the
     Intelligems flag). Read at every plan, so an arm set later in the session is picked up. Never throws. */
  const T20_ATTR = '_jb_t20';
  function t20Arm() {
    const w = window.JBT20_ARM;
    return w === 'a' || w === 'b' ? w : '';
  }
  /* Find my shade attribution: newest finder result (last 30 days) -> "photo|Light Gray|2026-10-13". Never throws. */
  const SF_ATTR = '_jb_sf';
  function sfSummary() {
    try {
      const o = JSON.parse(window.localStorage.getItem('jb-shade-match') || 'null');
      const it = o && Array.isArray(o.items) && o.items[0];
      if (!it || !it.shades || !it.shades[0] || !it.ts || Date.now() - it.ts > 30 * 864e5) return '';
      const name = String(it.shades[0].name || '').replace(/\|/g, ' ').slice(0, 60);
      return [it.method === 'photo' ? 'photo' : 'questions', name, new Date(it.ts).toISOString().slice(0, 10)].join('|');
    } catch (e) {
      return '';
    }
  }
  /* Messy Bun page (sections/jb-pdp-v3) bundle codes. Created in Shopify admin and tested on a real cart:
     2 buns with JBM2 = $55.98; 3 buns + brush with JBM3 + JBMB = $77.97 */
  const MB_PID = 7653118279893;
  const MB_CODE_2 = 'JBM2-K7Q4-RW8X'; // $7 off each Messy Bun, min 2
  const MB_CODE_3 = 'JBM3-Q8D4-ZX5M'; // $27 off the order, min 3 items
  const MB_CODE_GIFT = 'JBMB-F3R5-NB2C'; // buy 3 Messy Bun, get the Boar Bristle Brush free (combines with JBM3)
  /* "The Complete Look" set (JB36, snippets/jb-set-card.liquid + assets/jb-set.js): 3 pieces with the same line property
     _jb_set for SET_PRICE. One order code per possible regular total of the 3 pieces (Messy Bun $34.99, Premium $44.99,
     Small $33.99, Claw Clip Beach Waves 12" $45.90): key = regular total - SET_PRICE, in cents. Each code: fixed amount off
     the order, minimum subtotal = that regular total, combines with product + shipping discounts (like JBM3).
     If a price changes, the total has no code and the set simply is not discounted: update the table and the codes. */
  const SET_PRICE = 6999;
  const SET_CODES = {
    3198: 'JBS3-3198-GWHD',
    3298: 'JBS3-3298-NV32',
    3398: 'JBS3-3398-JMJC',
    3498: 'JBS3-3498-Z3Z5',
    4298: 'JBS3-4298-2WEW',
    4389: 'JBS3-4389-NXQX',
    4398: 'JBS3-4398-FT9N',
    4489: 'JBS3-4489-U5QR',
    4498: 'JBS3-4498-HJ7F',
    4589: 'JBS3-4589-X3VT',
    5398: 'JBS3-5398-5ZYF',
    5489: 'JBS3-5489-8336',
    5498: 'JBS3-5498-JPT5',
    5580: 'JBS3-5580-J29P',
    5589: 'JBS3-5589-3Q3V',
    5680: 'JBS3-5680-J8WJ',
    6498: 'JBS3-6498-TJUX',
    6589: 'JBS3-6589-MTA5',
    6680: 'JBS3-6680-HWLF',
    6771: 'JBS3-6771-ZR4S',
    // 3 Looks product page (JB36 C, Oct 2026): Messy (juvabun-messy-bun-set) $34.99, Curly $45.90, Sleek (Drawstring) $33.90.
    // Totals made of 34.99 / 45.90 only already have a code above; these are the ones with a Sleek in them.
    3171: 'JBS3-3171-3XRT',
    3280: 'JBS3-3280-KJUK',
    3389: 'JBS3-3389-54VV',
    4371: 'JBS3-4371-XHAW',
    4480: 'JBL3-4480-QK7M',
    5571: 'JBS3-5571-XVF4'
  };
  // A set she cut down to 2 pieces on the page or in the cart: $7 off (product code on the set products, min 2 units)
  const SET2_CODE = 'JBS2-0700-PL7J';
  const SET_CODE_LIST = Object.values(SET_CODES).concat(SET2_CODE);
  /* The bun wardrobe page (page body with #jbx) runs its own cart, drawer and codes: we stay hands off there */
  const isWardrobe = () => { const x = document.getElementById('jbx'); return !!x && !x.hasAttribute('data-jb-theme-cart'); };
  /* Club / Pass membership lines (see header): never "real" goods for protection or free shipping */
  const MEMB_HANDLES = ['juvabun-club', 'juvabun-pass'];
  const MEMB_SKUS = ['BUN-CLUB', 'BUN-PASS-90'];
  const isMembership = (i) => !!i && (MEMB_HANDLES.includes(String(i.handle || '')) || MEMB_SKUS.includes(String(i.sku || '').toUpperCase()));
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const net = {
    queue: Promise.resolve(),
    lastCart: null,
    lastCartAt: 0,
    hiddenAt: 0,
    warming: null,

    enqueue(task) {
      const p = this.queue.then(task, task);
      this.queue = p.catch(() => {});
      return p;
    },

    fetch(url, init, timeout) {
      const opts = Object.assign({ cache: 'no-store', credentials: 'same-origin', keepalive: false }, init);
      if (!timeout || typeof AbortController === 'undefined') return fetch(url, opts);
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), timeout);
      opts.signal = ac.signal;
      return fetch(url, opts).finally(() => clearTimeout(timer));
    },

    /* Keep the newest full cart we have seen (cart.js, change.js and update.js all return one; add.js does not) */
    remember(cart) {
      if (cart && Array.isArray(cart.items) && typeof cart.item_count === 'number') {
        const copy = Object.assign({}, cart);
        delete copy.sections;
        this.lastCart = copy;
        this.lastCartAt = Date.now();
      }
    },

    async getCart(timeout = 8000) {
      const res = await this.fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }, timeout);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const cart = await res.json();
      this.remember(cart);
      return cart;
    },

    /* Cheap GET that also replaces a dead pooled connection (used when the tab comes back after a while) */
    warm() {
      if (!this.warming) {
        this.warming = this.getCart(6000).catch(() => null).then((c) => { this.warming = null; return c; });
      }
      return this.warming;
    },

    async snapshot() {
      if (this.warming) await this.warming;
      if (this.lastCart && Date.now() - this.lastCartAt < SNAPSHOT_MAX_AGE) return this.lastCart;
      try { return await this.getCart(4000); } catch (e) { return this.lastCart; }
    },

    /* Did this add reach the cart? true / false / null (could not tell: cart.js unreachable) */
    async addLanded(items, before) {
      await sleep(CHECK_DELAY);
      let after;
      try { after = await this.getCart(6000); } catch (e) { return null; }
      const groups = new Map();
      (items || []).forEach((it) => {
        const props = it.properties || {};
        const k = String(it.id) + '|' + JSON.stringify(props);
        const g = groups.get(k) || { id: Number(it.id), props, qty: 0 };
        g.qty += Number(it.quantity) || 1;
        groups.set(k, g);
      });
      const sum = (cart, g) => ((cart && cart.items) || []).reduce((s, line) => {
        if (Number(line.variant_id || line.id) !== g.id) return s;
        const lp = line.properties || {};
        // Extra properties added by apps (e.g. Intelligems) are fine; ours must all match
        const ok = Object.keys(g.props).every((key) => String(lp[key]) === String(g.props[key]));
        return ok ? s + (Number(line.quantity) || 0) : s;
      }, 0);
      let anyUp = false;
      let anyShort = false;
      groups.forEach((g) => {
        const a = sum(after, g);
        if (before) {
          if (a > sum(before, g)) anyUp = true;
        } else if (a < g.qty) {
          anyShort = true;
        }
      });
      // Any increase means Shopify processed the request (adds are all-or-nothing), never send it twice
      if (before) return anyUp;
      // No snapshot from before the add: only "clearly missing" is certain, anything else stays unknown
      // (the items may have been in the cart already), so we never risk a second add
      return anyShort ? false : null;
    },

    /* One cart POST with recovery. NOT queued (callers run it inside enqueue).
       opts.add = items for an add.js call (non-idempotent: verify before re-sending).
       Resolves { json, recovered }; json is null when the request went through but its response was lost. */
    async post(path, payload, opts = {}) {
      const isAdd = !!opts.add;
      const before = isAdd ? await this.snapshot() : null;
      let unsure = false; // an add may have landed and we could not check yet
      let failedOnce = false;
      for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
        if (attempt) await sleep(RETRY_DELAYS[attempt - 1]);
        if (unsure) {
          const landed = await this.addLanded(opts.add, before);
          if (landed) return { json: null, recovered: true };
          if (landed === null) continue; // still cannot see the cart: do not risk a second add
          unsure = false;
        }
        let res;
        try {
          res = await this.fetch(ROOT + path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(payload)
          }, POST_TIMEOUT);
        } catch (e) {
          // Network-level failure (Safari "Load failed", aborted, offline): no HTTP response at all
          failedOnce = true;
          if (isAdd) {
            const landed = await this.addLanded(opts.add, before);
            if (landed) return { json: null, recovered: true };
            if (landed === null) unsure = true;
          }
          continue;
        }
        let json = null;
        try { json = await res.json(); } catch (e) { json = null; }
        if (res.ok && !(json && json.status && json.status >= 400)) {
          // 200: Shopify applied it, even if the body got cut off
          this.remember(json);
          return { json, recovered: !json };
        }
        if (res.status === 429 || res.status >= 500) {
          failedOnce = true;
          if (isAdd) {
            const landed = await this.addLanded(opts.add, before);
            if (landed) return { json: null, recovered: true };
            if (landed === null) unsure = true;
          }
          continue;
        }
        // A quantity change re-sent after a lost response can be refused because the first one already applied
        // (e.g. the removed line no longer exists): the caller re-renders from the real cart instead
        if (!isAdd && failedOnce) return { json: null, recovered: true };
        const err = new Error((json && (json.description || json.message)) || GENERIC_MSG);
        err.status = res.status;
        err.json = json;
        err.jbUser = true;
        throw err;
      }
      if (unsure && isAdd) {
        const landed = await this.addLanded(opts.add, before);
        if (landed) return { json: null, recovered: true };
      }
      const err = new Error(NET_FAIL_MSG);
      err.network = true;
      err.jbUser = true;
      throw err;
    },

    /* Public add: queued, recovered, then a fresh cart.js for the theme's events. Shipping protection (default on)
       and the bundle code are reconciled in the same queued task, so the drawer opens already complete (no flicker).
       Resolves { items, sections, cart, recovered } (sections null when we have no fresh HTML; listeners refresh) */
    add(items, { sections = true } = {}) {
      return this.enqueue(async () => {
        const payload = { items };
        const ids = sections ? store.sectionIds() : [];
        if (ids.length) { payload.sections = ids.join(','); payload.sections_url = window.location.pathname; }
        const r = await this.post('cart/add.js', payload, { add: items });
        let cart = null;
        try { cart = await this.getCart(6000); } catch (e) {
          try { await sleep(400); cart = await this.getCart(6000); } catch (e2) { cart = null; }
        }
        let html = (r.json && r.json.sections) || null;
        if (cart) {
          const rec = await store.reconcile(cart, ids);
          if (rec.ran) {
            html = (rec.json && rec.json.sections) || null;
            if (rec.json) cart = this.lastCart;
          }
        }
        return {
          items: (r.json && r.json.items) || items,
          sections: html,
          cart: cart || this.lastCart,
          recovered: r.recovered
        };
      });
    },

    /* Generic serialized cart write with the same recovery (cart/update.js, cart/change.js, or add.js with
       opts.add = items). Resolves { json, recovered }. */
    safePost(path, body, opts = {}) {
      const o = Object.assign({}, opts);
      if (/add\.js$/.test(path) && !o.add && body && Array.isArray(body.items)) o.add = body.items;
      return this.enqueue(() => this.post(path, body, o));
    },

    /* Text that is safe to show: Shopify's description (422) or our friendly copy. Never raw browser errors. */
    message(err) {
      return err && err.jbUser && err.message ? err.message : GENERIC_MSG;
    }
  };

  /* --------------------------------------------------------------- store */
  const store = {
    seq: 0,
    pending: new Map(), // line key -> { qty, timer }
    lastFix: '',
    pendingOpen: false,
    openTimer: null,

    components() {
      return Array.from(document.querySelectorAll('[data-jbc-section]'));
    },

    sectionIds() {
      return this.components().map((el) => el.dataset.sectionId).filter(Boolean);
    },

    drawer() {
      return document.querySelector('jb-cart-drawer');
    },

    page() {
      return document.querySelector('jb-cart-page');
    },

    /* POST a cart mutation with sections; serialized so responses never interleave. Protection and the bundle
       code are reconciled in the same queued task, so the shopper sees one consistent render. */
    mutate(path, payload) {
      const mySeq = ++this.seq;
      const p = net.enqueue(async () => {
        const ids = this.sectionIds();
        const body = Object.assign({}, payload, { sections: ids.join(','), sections_url: window.location.pathname });
        const r = await net.post(path, body, /add\.js$/.test(path) ? { add: payload.items } : {});
        let json = r.json;
        // change.js / update.js answer with the full cart; add.js does not
        let cart = json && Array.isArray(json.items) && typeof json.item_count === 'number' ? json : null;
        if (!cart) { try { cart = await net.getCart(6000); } catch (e) { cart = null; } }
        if (cart && this.pending.size === 0) {
          const rec = await this.reconcile(cart, ids);
          if (rec.ran) json = rec.json;
        }
        // Only paint the newest response, older ones would flash stale numbers
        if (mySeq === this.seq && this.pending.size === 0) {
          if (json && json.sections) {
            this.render(json.sections);
            this.afterRender(json);
          } else {
            // It went through but we have no HTML for it (response lost): fetch fresh sections
            await this.refresh();
          }
        }
        return json;
      });
      return p.catch((err) => {
        this.showError(net.message(err));
        return this.refresh();
      });
    },

    /* GET sections only (used after errors or external changes we have no HTML for) */
    async refresh() {
      const ids = this.sectionIds();
      if (!ids.length) return;
      try {
        const res = await net.fetch(`${ROOT}?sections=${encodeURIComponent(ids.join(','))}`, { headers: { Accept: 'application/json' } }, 10000);
        const sections = await res.json();
        this.render(sections);
        this.afterRender(null);
      } catch (e) {
        /* network issue: leave the UI as is */
      }
    },

    render(sections) {
      if (!sections) return;
      this.components().forEach((component) => {
        const html = sections[component.dataset.sectionId];
        if (html) component.renderFrom(html);
      });
    },

    afterRender(cartJson) {
      const count = this.realCount();
      this.syncCounts(count);
      this.announce();
      if (cartJson) {
        // Let the theme (header <cart-count>, other listeners) know, with the real product count
        const cart = Object.assign({}, cartJson, { item_count: count });
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'jb-cart:change', cart, jbc: true }
        }));
        setTimeout(() => this.syncCounts(count), 0);
      }
      this.scheduleReconcile();
    },

    /* ------------- reconcile: shipping protection (on by default) + bundle builder discount code
       Decided from a real cart object (never from rendered HTML), inside the shared queue, in ONE cart/update.js call.
       Protection: products in the cart and no opt-out -> exactly one protection line (regular variant, or "Free Order"
       when the only products are the free-today bun). Opt-out = cart attribute _jb_prot_optout=1 (localStorage as a
       backup for the same cart token). No products -> no protection. Never adds it twice: one line, quantity 1.
       Bundle code: the hidden Buy 2 Get 2 code is in the cart ONLY while a builder bundle (_jb_bb lines) is in it.
       Messy Bun codes (units of product MB_PID with property _jb_bundle):
         3+ units with _jb_bundle "3"  -> JBM3 + JBMB (3 for $77.97 + free brush)
         else 2+ units with _jb_bundle -> JBM2 ($7 off each bun; also when a 3-pack drops to 2)
         else                          -> none of the three
       The free brush line (_jb_gift "1", added by the Messy Bun page with 3 buns) is removed once fewer than 3
       "3 bun" units remain. Codes we do not manage are always kept, in their order.
       The same correction is never sent twice for the same cart, so a refused change can not loop.
       On the bun wardrobe page (#jbx) nothing is planned at all. */
    mbPlan(items) {
      let n3 = 0;
      let nAll = 0;
      items.forEach((i) => {
        if (Number(i.product_id) !== MB_PID) return;
        const b = i.properties && i.properties._jb_bundle;
        if (!b) return;
        const q = Number(i.quantity) || 0;
        nAll += q;
        if (String(b) === '3') n3 += q;
      });
      const codes = n3 >= 3 ? [MB_CODE_3, MB_CODE_GIFT] : nAll >= 2 ? [MB_CODE_2] : [];
      const dropGift = n3 >= 3 ? [] : items
        .filter((i) => i.properties && String(i.properties._jb_gift) === '1' && Number(i.quantity) > 0)
        .map((i) => i.key)
        .filter(Boolean);
      // The Complete Look / 3 Looks: the first set id with exactly 3 units gets its code (an order code: it replaces JBM3);
      // else a set id with exactly 2 units gets the $7 code (she removed a piece on the page or in the cart)
      const sets = new Map();
      items.forEach((i) => {
        const id = i.properties && i.properties._jb_set;
        if (!id) return;
        const g = sets.get(id) || { n: 0, sum: 0 };
        const q = Number(i.quantity) || 0;
        g.n += q;
        g.sum += (Number(i.original_price) || Number(i.price) || 0) * q;
        sets.set(id, g);
      });
      let setCode = null;
      for (const g of sets.values()) {
        const c = g.n === 3 ? SET_CODES[g.sum - SET_PRICE] : null;
        if (c) { setCode = c; break; }
      }
      if (!setCode) for (const g of sets.values()) { if (g.n === 2) { setCode = SET2_CODE; break; } }
      if (setCode) {
        const k = codes.indexOf(MB_CODE_3);
        if (k >= 0 && setCode !== SET2_CODE) codes.splice(k, 1);
        if (!codes.includes(setCode)) codes.push(setCode);
      }
      return { codes, dropGift };
    },

    protConfig() {
      const root = document.querySelector('[data-jbc-root] [data-real-count]');
      if (!root || !root.dataset.protDefault) return null;
      // T20 (snippets/jb-t20-loader.liquid): arm B uses the $3.49 variant (data-prot-b); arm A, no flag, or a theme
      // without the B variant uses the regular $2.99 one (data-prot-a; older renders only have data-prot-default).
      const arm = t20Arm();
      const a = Number(root.dataset.protA) || Number(root.dataset.protDefault) || 0;
      const b = Number(root.dataset.protB) || 0;
      return {
        pid: Number(root.dataset.protProduct) || 0,
        def: arm === 'b' && b ? b : a,
        free: Number(root.dataset.protFree) || 0,
        arm: arm === 'b' && b ? 'b' : (arm === 'a' ? 'a' : '')
      };
    },

    isProt(item, cfg) {
      return (cfg.pid && Number(item.product_id) === cfg.pid) || item.product_title === 'Shipping Protection';
    },

    token(cart) {
      return cart && cart.token ? String(cart.token).split('?')[0] : '';
    },

    lsOptout(cart) {
      const t = this.token(cart);
      if (!t) return false;
      try { return window.localStorage.getItem(OPTOUT_LS) === t; } catch (e) { return false; }
    },

    setLsOptout(cart, on) {
      try {
        if (on) { const t = this.token(cart); if (t) window.localStorage.setItem(OPTOUT_LS, t); }
        else window.localStorage.removeItem(OPTOUT_LS);
      } catch (e) { /* storage blocked: the cart attribute is the source of truth */ }
    },

    plan(cart) {
      if (isWardrobe()) return null;
      const body = {};
      const items = (cart && cart.items) || [];
      const cfg = this.protConfig();
      if (cfg && cfg.def) {
        const protLines = items.filter((i) => this.isProt(i, cfg));
        const real = items.filter((i) => !this.isProt(i, cfg) && !isMembership(i));
        const realCount = real.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
        const freeOnly = realCount > 0 && real.every((i) => Number(i.product_id) === FREE_TODAY_PID || /free-today/.test(String(i.handle || '')));
        const want = freeOnly && cfg.free ? cfg.free : cfg.def;
        const attrOpt = !!(cart.attributes && String(cart.attributes[OPTOUT_ATTR]) === '1');
        const lsOpt = !attrOpt && this.lsOptout(cart);
        // Bundle builder buys go straight to checkout: never auto-add protection to a cart that holds builder
        // lines (owner decision). An existing line is kept (she ticked it herself, or it came from a normal add).
        const hasBB = real.some((i) => i.properties && i.properties._jb_bb);
        // Keep an existing line (she chose it, or we added it); add one only when she has not opted out
        const keep = realCount > 0 && (protLines.length > 0 || !(attrOpt || lsOpt || hasBB));
        const updates = {};
        let has = false;
        protLines.forEach((l) => {
          const vid = Number(l.variant_id || l.id);
          const q = keep && vid === want && !has ? 1 : 0;
          if (q) has = true;
          if (Number(l.quantity) !== q) updates[vid] = q;
        });
        if (keep && !has) updates[want] = 1;
        if (Object.keys(updates).length) body.updates = updates;
        // localStorage says she opted out of this cart but the attribute got lost: restore it, never re-add
        if (lsOpt && realCount > 0 && !protLines.length) body.attributes = { [OPTOUT_ATTR]: '1' };
        // T20: the cart attribute _jb_t20 always equals this browser's arm ('' = no test), so the cart body
        // (Liquid) prints the same price as the line kept above, and the order records the arm. Sent in the same
        // request as the line swap; never on an empty cart.
        const t20Have = (cart.attributes && cart.attributes[T20_ATTR]) ? String(cart.attributes[T20_ATTR]) : '';
        if (items.length && t20Have !== cfg.arm) body.attributes = Object.assign({}, body.attributes, { [T20_ATTR]: cfg.arm });
      }
      // Free brush no longer earned: remove its line (by line key) in the same request
      const mb = this.mbPlan(items);
      if (mb.dropGift.length) {
        body.updates = body.updates || {};
        mb.dropGift.forEach((key) => { body.updates[key] = 0; });
      }
      // Discount codes: add / remove only the ones we manage, keep every other code as it is
      const code = (window.JB_BUNDLE_CODE || 'JBX-B2G2-7KQ4-WM9R').toUpperCase();
      const managed = [code, MB_CODE_2, MB_CODE_3, MB_CODE_GIFT].concat(SET_CODE_LIST);
      const want = mb.codes.slice();
      if (items.some((i) => i.properties && i.properties._jb_bb)) want.unshift(code);
      const codes = (cart.discount_codes || []).map((d) => String(d.code)).filter(Boolean);
      const foreign = codes.filter((c) => !managed.includes(c.toUpperCase()));
      const have = codes.filter((c) => managed.includes(c.toUpperCase())).map((c) => c.toUpperCase());
      const same = have.length === want.length && want.every((c) => have.includes(c));
      if (!same) body.discount = foreign.concat(want).join(',');
      if (!Object.keys(body).length) return null;
      // Find my shade attribution (owner, Oct 2026): rides along ONLY on an update that is already being sent
      // (never an extra request). Cart attribute _jb_sf = "method|matched shade|date" from the finder's memory
      // (localStorage jb-shade-match, written by jb-shade-finder.js). Lands on the order for reporting.
      const sf = cart.attributes && cart.attributes[SF_ATTR] ? '' : sfSummary();
      if (sf && items.length) body.attributes = Object.assign({}, body.attributes, { [SF_ATTR]: sf });
      const sig = JSON.stringify(body) + '|' + items.map((i) => i.key + 'x' + i.quantity).join(',') + '|' + JSON.stringify(cart.attributes || {});
      if (sig === this._lastPlan) return null;
      this._lastPlan = sig;
      return body;
    },

    /* Run INSIDE a queued task. Resolves { ran, json } (json null if the response was lost). Never shows errors. */
    async reconcile(cart, ids) {
      const body = cart ? this.plan(cart) : null;
      if (!body) return { ran: false, json: null };
      if (ids && ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }
      try {
        const r = await net.post('cart/update.js', body);
        return { ran: true, json: r.json };
      } catch (e) {
        return { ran: true, json: null };
      }
    },

    /* After any render: one coalesced check in the queue (uses the cart we just got when it is fresh) */
    scheduleReconcile() {
      if (this._recQueued) return;
      this._recQueued = true;
      net.enqueue(async () => {
        this._recQueued = false;
        let cart = net.lastCart && Date.now() - net.lastCartAt < 1500 ? net.lastCart : null;
        if (!cart) cart = await net.getCart(8000);
        const ids = this.sectionIds();
        const rec = await this.reconcile(cart, ids);
        if (!rec.ran) return;
        if (rec.json && rec.json.sections) {
          this.render(rec.json.sections);
          this.afterRender(rec.json);
        } else {
          await this.refresh();
        }
      }).catch(() => {
        /* network issue: leave the cart as is, the next change re-checks */
        this._recQueued = false;
      });
    },

    realCount() {
      const root = document.querySelector('[data-jbc-root] [data-real-count]');
      return root ? parseInt(root.dataset.realCount, 10) || 0 : null;
    },

    syncCounts(count) {
      if (count === null || count === undefined) return;
      document.querySelectorAll('cart-count').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('opacity-0', count === 0);
      });
    },

    announce() {
      const root = document.querySelector('[data-jbc-root] [data-real-count]');
      if (!root) return;
      const count = parseInt(root.dataset.realCount, 10) || 0;
      const total = root.querySelector('[data-jbc-total]');
      const ship = root.querySelector('[data-jbc-ship-msg]');
      const msg = count === 0
        ? 'Your cart is empty.'
        : `Cart updated. ${count} ${count === 1 ? 'item' : 'items'}. Total ${total ? total.textContent.trim() : ''}. ${ship ? ship.textContent.trim() : ''}`;
      this.components().forEach((c) => {
        const live = c.querySelector('[data-jbc-status]');
        if (live) live.textContent = msg;
      });
    },

    showError(message) {
      this.components().forEach((c) => {
        const el = c.querySelector('[data-jbc-error]');
        if (!el) return;
        el.textContent = message;
        el.hidden = false;
        clearTimeout(el._t);
        el._t = setTimeout(() => { el.hidden = true; }, 6000);
      });
    },

    /* ---------------- quantity: optimistic + debounced, one request per burst of taps */
    setQuantity(key, qty) {
      const entry = this.pending.get(key) || {};
      clearTimeout(entry.timer);
      entry.qty = qty;
      entry.timer = setTimeout(() => {
        this.pending.delete(key);
        this.mutate('cart/change.js', { id: key, quantity: entry.qty });
      }, qty === 0 ? 0 : DEBOUNCE_MS);
      this.pending.set(key, entry);
    },

    /* ---------------- events from the theme / other scripts */
    onExternalChange(event) {
      const detail = event.detail || {};
      if (detail.jbc) return;
      const cart = detail.cart;
      // Someone else changed the cart: use their full cart as our snapshot, or mark ours stale
      if (cart && Array.isArray(cart.items) && typeof cart.item_count === 'number' && cart.token) net.remember(cart);
      else net.lastCartAt = 0;
      const isAdd = detail.baseEvent === 'variant:add' || this.pendingOpen;
      const sections = cart && cart.sections;
      const ids = this.sectionIds();
      const done = () => {
        if (isAdd) this.openAfterAdd();
      };
      if (sections && ids.every((id) => sections[id])) {
        this.render(sections);
        this.afterRender(null);
        setTimeout(() => this.syncCounts(this.realCount()), 0);
        done();
      } else {
        this.refresh().then(() => {
          this.syncCounts(this.realCount());
          done();
        });
      }
    },

    onVariantAdd() {
      this.pendingOpen = true;
      clearTimeout(this.openTimer);
      // Fallback if no cart:change follows (forms that only fire variant:add)
      this.openTimer = setTimeout(() => {
        if (this.pendingOpen) this.refresh().then(() => this.openAfterAdd());
      }, 1200);
    },

    openAfterAdd() {
      this.pendingOpen = false;
      clearTimeout(this.openTimer);
      if (isWardrobe()) return; // the wardrobe page opens its own cart
      const drawer = this.drawer();
      if (drawer && !this.page() && !drawer.hasAttribute('data-on-cart-page')) drawer.show();
    }
  };

  /* --------------------------------------------------------- base element */
  class JBCartBase extends HTMLElement {
    connectedCallback() {
      this._ac = new AbortController();
      const opts = { signal: this._ac.signal };
      this.addEventListener('click', (e) => this.onClick(e), opts);
      this.addEventListener('change', (e) => this.onChange(e), opts);
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
    }

    get root() {
      return this.querySelector('[data-jbc-root]');
    }

    renderFrom(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-jbc-root]');
      const root = this.root;
      if (!fresh || !root) return;

      // Preserve focus and typed values across the re-render
      const active = document.activeElement;
      const focusKey = active && root.contains(active) ? active.getAttribute('data-jbc-focus') : null;
      const values = {};
      root.querySelectorAll('textarea[id]').forEach((t) => { values[t.id] = t.value; });

      root.innerHTML = fresh.innerHTML;

      Object.keys(values).forEach((id) => {
        const t = root.querySelector(`#${CSS.escape(id)}`);
        if (t) t.value = values[id];
      });
      if (focusKey) {
        const target = root.querySelector(`[data-jbc-focus="${CSS.escape(focusKey)}"]`);
        if (target) target.focus({ preventScroll: true });
        else this.focusFallback();
      } else if (active && active !== document.body && !document.contains(active)) {
        this.focusFallback();
      }
    }

    focusFallback() {
      const target = this.root && (this.root.querySelector('.jbc-qty__btn, .jbc-checkout, .jbc-empty .jbc-btn') || this.root.querySelector('[tabindex="-1"]'));
      if (target) target.focus({ preventScroll: true });
    }

    onClick(event) {
      const step = event.target.closest('[data-jbc-step]');
      if (step && this.contains(step)) {
        event.preventDefault();
        this.step(step);
        return;
      }
      const bundleRemove = event.target.closest('[data-jbc-bundle-remove]');
      if (bundleRemove && this.contains(bundleRemove)) {
        event.preventDefault();
        this.removeBundle(bundleRemove.dataset.jbcBundleRemove);
        return;
      }
      const remove = event.target.closest('[data-jbc-remove]');
      if (remove && this.contains(remove)) {
        event.preventDefault();
        const line = remove.closest('[data-jbc-line]');
        // A part of a "Build your bundle" bundle never leaves alone: the whole bundle goes
        if (line && line.dataset.bb) this.removeBundle(line.dataset.bb);
        else if (line) this.applyQuantity(line, 0);
        return;
      }
      const offer = event.target.closest('[data-jbc-offer-add]');
      if (offer && this.contains(offer)) {
        event.preventDefault();
        this.addOffer(offer);
      }
    }

    onChange(event) {
      const prot = event.target.closest('[data-jbc-protection]');
      if (prot) this.toggleProtection(prot);
    }

    step(button) {
      const line = button.closest('[data-jbc-line]');
      if (!line) return;
      const current = parseInt(line.dataset.qty, 10) || 0;
      const max = line.dataset.max ? parseInt(line.dataset.max, 10) : Infinity;
      let next = current + parseInt(button.dataset.jbcStep, 10);
      if (next > max) {
        store.showError(`Sorry, only ${max} available.`);
        return;
      }
      if (next < 0) next = 0;
      this.applyQuantity(line, next);
    }

    /* Optimistic update of line, totals, progress bar and counts, then one request */
    applyQuantity(line, qty) {
      const key = line.dataset.key;
      line.dataset.qty = qty;
      const unit = parseInt(line.dataset.unit, 10) || 0;
      const unitCompare = parseInt(line.dataset.unitCompare, 10) || 0;
      const qtyEl = line.querySelector('[data-jbc-qty]');
      if (qtyEl) qtyEl.textContent = qty;
      const priceEl = line.querySelector('[data-jbc-line-price]');
      if (priceEl) priceEl.textContent = formatMoney(unit * qty);
      const compareEl = line.querySelector('[data-jbc-line-compare]');
      if (compareEl) compareEl.lastChild.textContent = formatMoney(unitCompare * qty);
      const saveEl = line.querySelector('[data-jbc-line-save]');
      if (saveEl) saveEl.textContent = `You save ${formatMoney((unitCompare - unit) * qty)}`;

      if (qty === 0) {
        line.classList.add('is-removing');
        if (reduceMotion()) line.hidden = true;
        else setTimeout(() => { line.hidden = true; }, 220);
      }

      // Mirror the change into the other component (drawer <-> page) and recompute both
      store.components().forEach((c) => {
        const twin = c.root && Array.from(c.root.querySelectorAll('[data-jbc-line]')).find((l) => l.dataset.key === key);
        if (twin && twin !== line) {
          twin.dataset.qty = qty;
          const q = twin.querySelector('[data-jbc-qty]');
          if (q) q.textContent = qty;
          if (qty === 0) twin.hidden = true;
        }
        if (c.recalc) c.recalc();
      });

      store.setQuantity(key, qty);
    }

    recalc() {
      const root = this.root && this.root.querySelector('[data-real-count]');
      if (!root) return;
      let subtotal = 0;
      let count = 0;
      let membSub = 0; // membership lines (data-memb): in the total and count, not in the free-shipping base
      let goods = 0;
      root.querySelectorAll('[data-jbc-line]').forEach((l) => {
        const q = parseInt(l.dataset.qty, 10) || 0;
        subtotal += (parseInt(l.dataset.unit, 10) || 0) * q;
        count += q;
        if (l.hasAttribute('data-memb')) membSub += (parseInt(l.dataset.unit, 10) || 0) * q;
        else goods += q;
      });
      const cartDiscount = parseInt(root.dataset.cartDiscount, 10) || 0;
      const prot = root.querySelector('[data-jbc-protection]');
      const protPrice = prot && prot.checked ? parseInt(prot.dataset.price, 10) || 0 : 0;

      const sub = root.querySelector('[data-jbc-subtotal]');
      if (sub) sub.textContent = formatMoney(subtotal);
      const total = root.querySelector('[data-jbc-total]');
      if (total) total.textContent = formatMoney(Math.max(0, subtotal - cartDiscount) + protPrice);
      root.dataset.realCount = count;
      root.querySelectorAll('.jbc-head__count').forEach((el) => {
        el.textContent = count > 0 ? `(${count} ${count === 1 ? 'item' : 'items'})` : '';
      });

      const ship = root.querySelector('[data-jbc-ship]');
      if (ship) {
        const threshold = parseInt(ship.dataset.threshold, 10) || 5500;
        const base = Math.max(0, subtotal - membSub - cartDiscount);
        ship.style.display = goods === 0 ? 'none' : ''; // membership only: nothing ships, no bar
        const remaining = threshold - base;
        const pct = Math.max(6, Math.min(100, Math.floor((base * 100) / threshold)));
        const fill = ship.querySelector('[data-jbc-ship-fill]');
        if (fill) fill.style.width = `${pct}%`;
        const track = ship.querySelector('[role="progressbar"]');
        if (track) track.setAttribute('aria-valuenow', pct);
        const msg = ship.querySelector('[data-jbc-ship-msg]');
        if (msg) {
          msg.innerHTML = remaining > 0
            ? `You're <strong>${formatMoney(remaining)}</strong> away from free US shipping`
            : '<span aria-hidden="true">&#10003;</span> You\'ve unlocked free US shipping';
        }
        ship.classList.toggle('is-done', remaining <= 0);
        const cost = root.querySelector('[data-jbc-ship-cost]');
        if (cost) cost.textContent = remaining <= 0 ? 'Free (US)' : 'Calculated at checkout';
      }
      store.syncCounts(count);
    }

    /* "Build your bundle": remove every line with this _jb_bb in ONE /cart/update.js request (keys -> 0) */
    removeBundle(id) {
      if (!id) return;
      const updates = {};
      store.components().forEach((c) => {
        if (!c.root) return;
        c.root.querySelectorAll('[data-jbc-line]').forEach((l) => {
          if (l.dataset.bb !== id) return;
          updates[l.dataset.key] = 0;
          l.dataset.qty = 0;
        });
        c.root.querySelectorAll('[data-jbc-bundle]').forEach((g) => {
          if (g.dataset.jbcBundle !== id) return;
          g.classList.add('is-removing');
          if (reduceMotion()) g.hidden = true;
          else setTimeout(() => { g.hidden = true; }, 220);
        });
        if (c.recalc) c.recalc();
      });
      if (!Object.keys(updates).length) return;
      store.mutate('cart/update.js', { updates });
    }

    /* Checked by default. Unchecking removes it and remembers the opt-out for this cart (cart attribute + localStorage);
       checking again adds it and clears the opt-out. Same serialized queue as every other cart call. */
    toggleProtection(input) {
      const root = this.root && this.root.querySelector('[data-real-count]');
      const wanted = input.checked;
      const variantId = input.dataset.variantId;
      const updates = {};
      // Remove any protection variant currently in the cart, then set the right one
      ((root && root.dataset.protVids) || '').split(',').filter(Boolean).forEach((vid) => { updates[vid] = 0; });
      updates[variantId] = wanted ? 1 : 0;
      // Mirror checkbox state in the other component and update totals right away
      store.components().forEach((c) => {
        const other = c.root && c.root.querySelector('[data-jbc-protection]');
        if (other && other !== input) other.checked = wanted;
        if (c.recalc) c.recalc();
      });
      if (!wanted) store.setLsOptout(net.lastCart, true);
      else store.setLsOptout(null, false);
      store.mutate('cart/update.js', { updates, attributes: { [OPTOUT_ATTR]: wanted ? '' : '1' } }).then((json) => {
        // Backup copy keyed by the cart token (known now even if it was not before)
        if (!wanted && json && json.token) store.setLsOptout(json, true);
      });
    }

    addOffer(button) {
      const wrap = button.closest('[data-jbc-offer]');
      const select = wrap && wrap.querySelector('[data-jbc-offer-variant]');
      const id = select ? select.value : button.dataset.variantId;
      if (!id) return;
      button.disabled = true;
      button.textContent = 'Adding...';
      // /cart/add.js returns the added items plus freshly rendered sections; Klaviyo tracks it like any add
      store.mutate('cart/add.js', { items: [{ id: Number(id), quantity: 1 }] });
    }
  }

  /* ---------------------------------------------------------------- page */
  class JBCartPage extends JBCartBase {}

  /* -------------------------------------------------------------- drawer */
  class JBCartDrawer extends JBCartBase {
    connectedCallback() {
      super.connectedCallback();
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      const opts = { signal: this._ac.signal };
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.hide(); }, opts);
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.hide(); // backdrop
        if (e.target.closest('[data-jbc-close]')) { e.preventDefault(); this.hide(); }
      }, opts);
      this.dialog.addEventListener('keydown', (e) => this.trapFocus(e), opts);
      // If the browser closes the dialog itself (e.g. repeated Esc), still clean up
      this.dialog.addEventListener('close', () => {
        this.dialog.classList.remove('is-open');
        document.documentElement.classList.remove('jbc-lock');
      }, opts);
    }

    get open() {
      return !!(this.dialog && this.dialog.open);
    }

    show() {
      if (!this.dialog || this.open) return;
      this._opener = document.activeElement;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.classList.add('jbc-lock');
      requestAnimationFrame(() => {
        this.dialog.classList.add('is-open');
        const heading = this.dialog.querySelector('#jbc-drawer-title');
        if (heading) heading.focus({ preventScroll: true });
      });
      this.dispatchEvent(new CustomEvent('dialog:after-show', { bubbles: true }));
    }

    hide() {
      if (!this.dialog || !this.open) return;
      this.dialog.classList.remove('is-open');
      const finish = () => {
        if (typeof this.dialog.close === 'function') this.dialog.close();
        else this.dialog.removeAttribute('open');
        document.documentElement.classList.remove('jbc-lock');
        if (this._opener && document.contains(this._opener)) this._opener.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('dialog:after-hide', { bubbles: true }));
      };
      if (reduceMotion()) finish();
      else setTimeout(finish, 260);
    }

    toggle() {
      this.open ? this.hide() : this.show();
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusables = Array.from(this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"]), iframe'))
        .filter((el) => !el.hidden && el.getClientRects().length > 0);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement) || document.activeElement.id === 'jbc-drawer-title')) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('jb-cart-page')) customElements.define('jb-cart-page', JBCartPage);
  if (!customElements.get('jb-cart-drawer')) customElements.define('jb-cart-drawer', JBCartDrawer);

  /* ------------------------------------------------------ global listeners (once) */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[aria-controls="cart-drawer"]');
    if (!opener) return;
    const drawer = store.drawer();
    if (!drawer || store.page()) return; // on /cart the link just reloads the page
    event.preventDefault();
    drawer.toggle();
  });

  // Impact bundles requested sections into /cart/add.js when a component asks for it
  document.addEventListener('cart:prepare-bundled-sections', (event) => {
    if (event.detail && Array.isArray(event.detail.sections)) {
      store.sectionIds().forEach((id) => {
        if (!event.detail.sections.includes(id)) event.detail.sections.push(id);
      });
    }
  });

  document.addEventListener('variant:add', () => store.onVariantAdd());
  document.addEventListener('cart:change', (event) => store.onExternalChange(event));
  // Other scripts changed the cart: our snapshot is stale, the next reconcile reads /cart.js
  document.addEventListener('cart:refresh', () => { net.lastCartAt = 0; store.refresh(); });
  document.addEventListener('cart:updated', () => { net.lastCartAt = 0; store.pendingOpen = true; store.refresh().then(() => store.openAfterAdd()); });

  // Coming back from checkout with the back button (bfcache): the cart may have changed
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) { net.warm(); store.refresh(); }
  });

  // Back after > 60s in the background (or screen lock): iOS often keeps a dead pooled connection, and the next
  // POST on it fails with "Load failed". A cheap GET now replaces it and refreshes our cart snapshot.
  // Shorter absences only mark the snapshot stale (another tab may have changed the cart).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { net.hiddenAt = Date.now(); return; }
    const away = net.hiddenAt ? Date.now() - net.hiddenAt : 0;
    net.hiddenAt = 0;
    if (away > 60000) net.warm();
    else if (away > 3000) net.lastCartAt = 0;
  });
  window.addEventListener('online', () => net.warm());

  // Theme editor: open the drawer when its section is selected
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = store.drawer();
    if (drawer && drawer.closest(`#shopify-section-${event.detail.sectionId}`)) drawer.show();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = store.drawer();
    if (drawer && drawer.closest(`#shopify-section-${event.detail.sectionId}`)) drawer.hide();
  });

  const init = () => {
    // The header renders cart.item_count (includes protection); correct it to products only
    store.syncCounts(store.realCount());
    // After load, when idle: protection / bundle code consistent with the cart (no effect on page speed)
    (window.requestIdleCallback || ((fn) => setTimeout(fn, 1500)))(() => store.scheduleReconcile());
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.JBCart = {
    store,
    net,
    formatMoney,
    open: () => store.drawer() && store.drawer().show(),
    refresh: () => store.refresh(),
    // Shared, serialized, self-recovering add used by every jb PDP and the bundle builder
    add: (items, opts) => net.add(items, opts),
    safePost: (path, body, opts) => net.safePost(path, body, opts),
    message: (err) => net.message(err)
  };
})();
